// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - One reusable page title row
// 2. Separation of Concerns - Presentation only; no data fetching
// 3. Modular Architecture - Shared across main app routes
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

/**
 * Standard page header: h4 title, optional description (body1), leading icon, optional actions.
 * Aligns with User Management visual standard (primary color, fontWeight 600, 32px icon).
 */

import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * @param {{ title: string, description?: string, icon?: React.ReactNode, actions?: React.ReactNode }} props
 */
const PageSectionHeader = ({ title, description, icon, actions }) => (
  <Box
    sx={{
      mb: 3,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: 2,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minWidth: 0,
        flex: '1 1 240px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          minWidth: 0,
        }}
      >
        {icon ? (
          <Box
            sx={{
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              lineHeight: 0,
              '& .MuiSvgIcon-root': { fontSize: 32 },
              '& svg': { fontSize: 32 },
            }}
          >
            {icon}
          </Box>
        ) : null}
        <Typography
          variant="h4"
          component="h1"
          sx={{
            color: 'primary.main',
            fontWeight: 600,
            lineHeight: 1.25,
            minWidth: 0,
          }}
        >
          {title}
        </Typography>
      </Box>
      {description ? (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mt: 0.25,
            // Align with 32px icon + gap: use spacing units (MUI v6 rejects theme fn in responsive pl → spacing.js errors)
            pl: icon ? { xs: 0, sm: 6 } : 0,
          }}
        >
          {description}
        </Typography>
      ) : null}
    </Box>
    {actions ? (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {actions}
      </Box>
    ) : null}
  </Box>
);

export default PageSectionHeader;
